import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setField } from 'actions/matrixSettings';
import {
  SettingsViewOnlyText,
  SettingsImage,
  SettingsDescription,
  SettingsButtonSet,
  SettingsToggle,
  SettingsTextField,
} from 'components/SettingsNew';
import { useValidateMatix } from 'hooks/matrices';
import './MatrixSettings.scss';
import { getMatrix, getExperiments } from 'constants/matrices';

const MatrixSettingsBasic = ({ matrix, base, isEditing }) => {
  const dispatch = useDispatch();
  const { validationMessages } = useSelector(state => state.matrixSettings);
  const { userData, user } = useSelector(state => state.auth);
  const { matrices } = useSelector(state => state.matrices);

  const canChangeIsPublic = useMemo(() => {
    if (!userData.isAdmin) return false;

    // If parent matrix is not public, no changing this experiment's publicity
    if (matrix.parentId && !getMatrix(matrices, matrix.parentId)?.isPublic) {
      return false;
    }

    // If matrix has any public children, no changing this matrix's publicity
    // (would leave some public children with a private parent.
    if (getExperiments(matrices, matrix).some(m => m.isPublic)) {
      return false;
    }

    return true;
  }, [matrix, matrices, userData]);

  const validateMatrix = useValidateMatix();

  useEffect(() => {
    validateMatrix();
  }, [matrix, validateMatrix]);

  return (
    <div className="MatrixSettingsBasic">
      <div className="MatrixSettings-section-title">Basic settings</div>

      <div className="MatrixSettings-row">
        <div className="MatrixSettings-column MatrixSettings-half">
          <SettingsImage
            disabled={!user}
            onChange={value => dispatch(setField('imageUrl', value))}
            value={matrix.imageUrl}
            description="Recommended photo size is no less than 450 x 300px"
            isStatic={!isEditing}
          />
        </div>
        <div className="MatrixSettings-column MatrixSettings-half">
          <div className="MatrixSettings-column MatrixSettings-half">
            <SettingsViewOnlyText label="Created by" text={matrix?.createdBy} />
          </div>
          <SettingsDescription
            label="Description"
            onChange={value => dispatch(setField('description', value))}
            value={matrix.description}
            validationMessage={validationMessages.description}
            placeholder={`Add a short description of your ${
              matrix.parentId !== null ? 'experiment' : 'matrix'
            }.`}
            isStatic={!isEditing}
          />{' '}
          {canChangeIsPublic ? (
            <SettingsButtonSet
              disabled={!canChangeIsPublic}
              label="Location"
              onChange={value => {
                dispatch(setField('isPublic', value === 'yes'));
                if (value === 'no') {
                  dispatch(setField('isTemplate', false));
                }
              }}
              options={[
                { value: 'yes', label: 'Public' },
                { value: 'no', label: 'Private' },
              ]}
              value={matrix.isPublic ? 'yes' : 'no'}
              description={
                'Public matrices are available in the public gallery to all users.'
              }
              isStatic={!isEditing}
            />
          ) : null}
        </div>
      </div>

      <div className="MatrixSettings-row">
        {base ? (
          <div className="MatrixSettings-column MatrixSettings-half">
            <SettingsViewOnlyText label="Base matrix" text={base?.name} />
          </div>
        ) : null}
      </div>
      {/* <div className="MatrixSettings-row">
        {canChangeIsPublic ? (
          <div className="MatrixSettings-column MatrixSettings-half">
            <SettingsButtonSet
              disabled={!canChangeIsPublic}
              label="Location"
              onChange={value => {
                dispatch(setField('isPublic', value === 'yes'));
                if (value === 'no') {
                  dispatch(setField('isTemplate', false));
                }
              }}
              options={[
                { value: 'yes', label: 'Public' },
                { value: 'no', label: 'Private' },
              ]}
              value={matrix.isPublic ? 'yes' : 'no'}
              description={
                'Public matrices are available in the public gallery to all users.'
              }
              isStatic={!isEditing}
            />
          </div>
        ) : null}
      </div> */}

      {canChangeIsPublic && matrix.isPublic ? (
        <>
          <div className="MatrixSettings-column">
            <div className="MatrixSettings-section-title">Template</div>

            <div className="MatrixSettings-row">
              <div className="MatrixSettings-column MatrixSettings-half">
                <SettingsToggle
                  label="Use as template"
                  disabled={!isEditing}
                  onChange={value => {
                    dispatch(setField('isTemplate', value));
                  }}
                  value={matrix?.isTemplate ?? false}
                  isStatic={!isEditing}
                />
              </div>
            </div>
          </div>
          <div className="MatrixSettings-row">
            {matrix?.isTemplate ? (
              <div className="MatrixSettings-column MatrixSettings-half">
                <SettingsTextField
                  label="Template name"
                  description="Short name for the template"
                  onChange={value => dispatch(setField('templateName', value))}
                  value={matrix?.templateName}
                  isStatic={!isEditing}
                />
                <SettingsTextField
                  label="Template location"
                  description="Location of the ecosystem"
                  onChange={value =>
                    dispatch(setField('templateLocation', value))
                  }
                  value={matrix?.templateLocation}
                  isStatic={!isEditing}
                />
              </div>
            ) : null}

            {matrix?.isTemplate ? (
              <div className="MatrixSettings-column MatrixSettings-half">
                <SettingsTextField
                  label="Template ecosystem"
                  description="Nature of the ecosystem"
                  onChange={value =>
                    dispatch(setField('templateEcosystem', value))
                  }
                  value={matrix?.templateEcosystem}
                  isStatic={!isEditing}
                />
                <SettingsDescription
                  label="Template purpose"
                  description="Best use case for starting with this template"
                  onChange={value =>
                    dispatch(setField('templatePurpose', value))
                  }
                  value={matrix?.templatePurpose}
                  isStatic={!isEditing}
                />
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

export default MatrixSettingsBasic;
